import { ReactNode, useMemo, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { media } from "@atlaskit/primitives";

import {
    getProductData,
    getSwitchAccountUrl,
    ProductData,
    ProductKey
} from "@/components/signup/utils";
import {
    useSitesWithCurrentProduct,
    useUserContext
} from "@/components/user-context";
import { ExistingSite } from "@/libraries/bxp-web-stargate-client/src";
import { Button } from "@/libraries/design/components";
import { wacToken } from "@/libraries/design/foundations";
import { useLocation } from "@/utils/location-provider";

import { useSiteActivationContext } from "../../hooks";
import { ExistingUser as DefaultExistingUser } from "../existing-user";

export const ReturnToProduct = ({
    ExistingUser = <DefaultExistingUser />,
    sites
}: {
    ExistingUser?: ReactNode;
    sites?: ExistingSite[];
}) => {
    const location = useLocation();
    const createNewWorkspace = useMemo(() => {
        const queryParams = new URLSearchParams(location.search);
        return queryParams.get("createNew") === "true";
    }, [location.search]);
    const [showSiteNaming, setShowSiteNaming] = useState(createNewWorkspace);
    const { product } = useUserContext();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const intl = useIntl();
    const sitesWithCurrentProduct = useSitesWithCurrentProduct();
    const { isWorkspaceProduct } = useSiteActivationContext();

    const {
        user: { email }
    } = useUserContext();
    const { productName } = getProductData(
        product?.key as keyof typeof ProductData
    );

    if (sites === undefined) {
        sites = sitesWithCurrentProduct;
    }

    const goToProductButtonText = isWorkspaceProduct
        ? intl.formatMessage({
              id: "bxpSignup.siteExpansion.goToWorkspace",
              defaultMessage: "Go to workspace"
          })
        : intl.formatMessage(
              {
                  id: "bxpSignup.siteExpansion.goToProduct",
                  defaultMessage: "Go to {productName}"
              },
              { productName }
          );

    const createNewWorkspaceMessageValues = {
        link: (str: ReactNode) =>
            createNewWorkspaceMessageLink(str, () => {
                setShowSiteNaming(true);
                createAnalyticsEvent({
                    eventType: "ui",
                    action: "clicked",
                    actionSubject: "button",
                    actionSubjectId: "contactExpandGoToSiteNamingScreen",
                    attributes: {
                        productName: product?.name
                    }
                }).fire();
            })
    };

    const createNewWorkspaceMessage = isWorkspaceProduct
        ? intl.formatMessage(
              {
                  id: "BxpSignup.SiteExpansionForm.cobrandedConfluenceCreateNewWorkspaceLink",
                  defaultMessage: "or <link>create a new workspace</link>"
              },
              createNewWorkspaceMessageValues
          )
        : intl.formatMessage(
              {
                  id: "BxpSignup.SiteExpansionForm.cobrandedJiraCreateNewWorkspaceLink",
                  defaultMessage: "or <link>start a new site</link>"
              },
              createNewWorkspaceMessageValues
          );

    const clickReturnToExistingWorkspace = () => {
        setShowSiteNaming(false);
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "contactExpandGoToReturnToProductScreen",
            attributes: {
                productName: product?.name
            }
        }).fire();
    };

    const goToExistingWorkspaceMessageValues = {
        link: (str: ReactNode) => link(str, clickReturnToExistingWorkspace)
    };

    const goToExistingWorkspaceMessage = isWorkspaceProduct
        ? intl.formatMessage(
              {
                  id: "BxpSignup.SiteExpansionForm.cobrandedGoToExistingWorkspace",
                  defaultMessage: "or <link>visit an existing workspace</link>"
              },
              goToExistingWorkspaceMessageValues
          )
        : intl.formatMessage(
              {
                  id: "bxpSignup.companyExpand.cobrandedReturnToJoinSites",
                  defaultMessage: "or <link>join an existing site</link>"
              },
              goToExistingWorkspaceMessageValues
          );

    return showSiteNaming ? (
        <div css={containerCss}>
            {ExistingUser}
            <div css={goToExistingWorkspaceMessageCss}>
                {goToExistingWorkspaceMessage}
            </div>
        </div>
    ) : (
        <div css={containerCss}>
            <div css={contentCss}>
                <p css={switchAccountSubheader}>
                    <FormattedMessage
                        id="BxpSignup.SiteExpansionForm.cobrandedSubheader.2"
                        defaultMessage="You’re logged in as {email}. <switchAccountLink>Switch account</switchAccountLink>"
                        values={{
                            email: <strong css={emailCss}>{email}</strong>,
                            switchAccountLink
                        }}
                    />
                </p>
                <div css={boxContainerCss} data-testid="return-to-product">
                    {sites?.map((site, index) => (
                        <a
                            key={index}
                            css={siteBoxCss}
                            href={getProductUrl({
                                url: site.url,
                                productKey: product?.key ?? ""
                            })}
                            onClick={(e) => {
                                e.preventDefault(); // wait for event to fire
                                createAnalyticsEvent({
                                    eventType: "ui",
                                    action: "clicked",
                                    actionSubject: "button",
                                    actionSubjectId:
                                        "contactExpandClickReturnToProductButton",
                                    attributes: {
                                        productName
                                    }
                                }).fire();
                                window.location.assign(
                                    getProductUrl({
                                        url: site.url,
                                        productKey: product?.key ?? ""
                                    })
                                );
                            }}
                        >
                            <div css={siteCss}>
                                <p css={bold}>{productName}</p>
                                <div css={siteNameCss}>{site.displayName}</div>
                            </div>
                            <div css={goToProductButtonCss}>
                                <Button
                                    variant="primary"
                                    size="small"
                                    color="blue"
                                >
                                    {goToProductButtonText}
                                </Button>
                            </div>
                        </a>
                    ))}
                </div>
            </div>
            <div css={createNewWorkspaceMessageCss}>
                {createNewWorkspaceMessage}
            </div>
        </div>
    );
};

const link = (str: ReactNode, clickReturnToExistingWorkspace: () => void) => (
    <button
        css={goToExistingWorkspaceLinkCss}
        onClick={clickReturnToExistingWorkspace}
    >
        {str}
    </button>
);

const createNewWorkspaceMessageLink = (
    str: ReactNode,
    exitView: () => void
) => (
    <button css={createNewWorkspaceLinkCss} onClick={exitView}>
        {str}
    </button>
);

const switchAccountLink = (children: ReactNode) => (
    <a href={getSwitchAccountUrl()} css={switchAccountLinkCss}>
        {children}
    </a>
);
const getProductUrl = ({
    url,
    productKey
}: {
    url: string;
    productKey: string;
}) => {
    switch (productKey) {
        case ProductKey.JIRA_SOFTWARE:
            return `${url}/jira`;
        case ProductKey.CONFLUENCE:
            return `${url}/wiki`;
        default:
            return url;
    }
};

const containerCss = css({
    alignContent: "space-between",
    display: "grid",
    flexGrow: 1,
    gap: 32
});

const goToExistingWorkspaceLinkCss = css({
    backgroundColor: "transparent !important",
    border: 0,
    borderRadius: 0,
    color: wacToken("color.primary.blue"),
    cursor: "pointer",
    fontSize: "inherit !important",
    fontWeight: "inherit",
    minHeight: "auto",
    minWidth: "auto",
    padding: "0 !important",
    textDecoration: "underline !important",
    [":hover"]: {
        color: `${wacToken("color.extended.blue800")} !important`
    }
});

const goToExistingWorkspaceMessageCss = css({
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center"
});

const switchAccountSubheader = css({
    fontSize: 14,
    margin: 0,
    textAlign: "center"
});

const contentCss = css({
    display: "grid",
    gap: 32
});

const createNewWorkspaceMessageCss = css({
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center"
});

const createNewWorkspaceLinkCss = css({
    backgroundColor: "transparent !important",
    border: 0,
    borderRadius: 0,
    color: wacToken("color.primary.blue"),
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: "inherit !important",
    fontWeight: "inherit",
    minHeight: "auto",
    minWidth: "auto",
    padding: "0 !important",
    textDecoration: "underline !important",
    [":hover"]: {
        color: wacToken("color.extended.blue800")
    }
});

const boxContainerCss = css({
    display: "grid",
    gap: 16,
    [media.above.sm]: {
        maxHeight: "20.75rem",
        overflowY: "auto"
    }
});

const siteBoxCss = css({
    alignItems: "center",
    border: `1px solid ${wacToken("color.neutral.light400")}`,
    borderRadius: 8,
    display: "grid",
    gap: 10,
    gridTemplateColumns: "1fr auto",
    justifyContent: "space-between",
    padding: 16,
    width: "100%",
    [":hover"]: {
        textDecoration: "none !important"
    }
});

const goToProductButtonCss = css({
    maxWidth: 180
});

const bold = css({
    fontWeight: "400",
    margin: 0
});

const siteCss = css({
    fontSize: 16,
    overflow: "hidden"
});

const siteNameCss = css({
    color: wacToken("color.neutral.light700"),
    fontSize: 14,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
});

const emailCss = css({
    fontWeight: 600,
    hyphens: "auto",
    overflowWrap: "break-word",
    wordBreak: "break-word",
    wordWrap: "break-word"
});

const switchAccountLinkCss = css({
    color: wacToken("color.primary.blue"),
    textDecoration: "underline",
    whiteSpace: "nowrap",
    [":hover"]: {
        color: wacToken("color.extended.blue800")
    }
});
